import { useCallback, useEffect, useMemo, useRef } from 'react';
import { FieldPath, FieldValues } from 'react-hook-form';

import { createListItems, ListValue, useItemsSearch } from '@xemplo/stateful-list-core';

import { UseDropdownRhfProps } from './hooks.types';
import { useSelectedLabel } from './use-selected-label';

export function useDropdownRhf<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>(props: UseDropdownRhfProps<TFieldValues, TName>) {
  const {
    fieldProps,
    items,
    searchable,
    onChange,
    onBlur,
    onSearchChange,
    valueLabel,
    showSelectedOnOpen,
    ...rest
  } = props;
  const { field, fieldState } = fieldProps;
  const value = field.value ?? '';

  const {
    renderItems,
    search: displayValue,
    valueItemMap,
    setSearch: setDisplayValue,
    instantSetSearch: setDisplayValueInstant,
    setSearchDisplayOnly,
    getItemLabel,
    // `searchable` must be coerced: `useItemsSearch` defaults an `undefined` flag to
    // true, which would filter the list of a dropdown that cannot even be typed into.
  } = useItemsSearch({ items, searchable: !!searchable && !onSearchChange });

  // The selected label lives in its own cache, not in the (server-search-churned) list, so
  // it survives every keystroke. `resolve` reads it synchronously; `label` is reactive so an
  // async-arriving option re-renders us.
  const {
    label: selectedLabel,
    resolve,
    capture,
  } = useSelectedLabel(value, getItemLabel, valueLabel);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setDisplayValue(e.target.value);
      onSearchChange?.(e.target.value);
    },
    [setDisplayValue, onSearchChange],
  );

  const handleListChange = useCallback(
    (selected: ListValue | undefined) => {
      const item = selected ? valueItemMap.get(selected) : undefined;
      if (item) capture(item.value, item.label); // keep the label past the list changing
      setDisplayValueInstant(item?.label ?? '');
      field.onChange(item?.value ?? ''); // Update the selected item state
      onChange?.(selected);
    },
    [valueItemMap, setDisplayValueInstant, field, onChange, capture],
  );

  const handleBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      field.onBlur();
      onBlur?.(e);
    },
    [field, onBlur],
  );

  // Drives the display text off the cached label instead of the live list, so list churn no
  // longer wipes a search-in-progress. Two triggers only:
  //  - the value changed (selection, RHF reset, prefill on mount): set it authoritatively;
  //  - the value is unchanged but its label just resolved (async options) AND the field is
  //    idle (display empty): fill it — never overwriting a live query.
  // Focus clearing the display is not a dependency, so it can't re-trigger a re-fill.
  const prevValueRef = useRef<ListValue | undefined>(undefined);
  const displayRef = useRef(displayValue);
  displayRef.current = displayValue;
  useEffect(() => {
    if (prevValueRef.current !== value) {
      prevValueRef.current = value;
      setSearchDisplayOnly(value ? resolve(value) : '');
      return;
    }
    if (selectedLabel && !displayRef.current) setSearchDisplayOnly(selectedLabel);
  }, [value, selectedLabel, resolve, setSearchDisplayOnly]);

  // While a value is selected and the field is idle, surface that option as a highlighted
  // row from the cache instead of the prompt/stale list. "Idle" covers both the
  // empty display (after the open-clear) AND the display still showing the selected label
  // (the instant the popup opens, before the clear flushes) — otherwise renderItems flashes
  // for a frame before the selected row takes over. Skipped once the user types a real query,
  // or when the live list already contains the value.
  const listItems = useMemo(() => {
    if (!showSelectedOnOpen || !value) return renderItems;
    const label = resolve(value);
    if (!label) return renderItems;
    const isIdle = !displayValue || displayValue === label;
    if (!isIdle) return renderItems;
    if (renderItems.valueItemMap?.has(value)) return renderItems;
    return createListItems()
      .addGroup((group) => group.addItem(value, label))
      .build();
  }, [showSelectedOnOpen, value, displayValue, renderItems, resolve]);

  return {
    ...rest,
    required: !!props.rules?.required,
    searchable,
    items: listItems,
    displayValue,
    setDisplayValue: setDisplayValueInstant,
    onSearchChange,
    onChange: handleInputChange,
    onListChange: handleListChange,
    onBlur: handleBlur,
    value,
    resolveLabel: resolve,
    errorMessage: fieldState.error?.message?.toString(),
  };
}
